import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import clsx from 'clsx';

import { api } from '../api';
import {
  CELL_STATS,
  DETAIL_STATS,
  MODELS,
  OVERLAYS,
  useWeatherPrefs,
  type WeatherLocation,
  type WeatherPrefs
} from '../weather';
import ConfirmDialog from './ConfirmDialog';
import Section from './SettingsSection';

interface GeocodeResult {
  name: string;
  region: string | null;
  latitude: number;
  longitude: number;
  timezone: string;
}

export default function WeatherSettings() {
  const qc = useQueryClient();
  const prefsQ = useWeatherPrefs();
  const prefs = prefsQ.data;
  const [removing, setRemoving] = useState<WeatherLocation | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['weather-prefs'] });
    qc.invalidateQueries({ queryKey: ['weather'] });
  };

  const update = useMutation({
    mutationFn: (patch: Partial<Omit<WeatherPrefs, 'locations'>>) =>
      api.patch<WeatherPrefs>('/api/weather/preferences', patch),
    onMutate: (patch) => {
      qc.setQueryData<WeatherPrefs>(['weather-prefs'], (old) => (old ? { ...old, ...patch } : old));
    },
    onSettled: invalidate
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/weather/locations/${id}`),
    onSettled: invalidate
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch(`/api/weather/locations/${id}`, { name }),
    onSettled: invalidate
  });

  if (!prefs) return null;

  function toggle<T extends string>(field: 'cell_stats' | 'detail_stats' | 'overlays', list: T[], id: T) {
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    update.mutate({ [field]: next });
  }

  return (
    <>
      <Section title="Locations">
        <p className="mb-3 text-[0.85rem] text-[var(--text-muted)]">
          The selected location is the one the calendar shows. Switch between them here or from the
          weather popover.
        </p>
        <div className="mb-3 flex flex-col divide-y divide-[var(--border)] overflow-hidden rounded-lg border border-[var(--border)]">
          {prefs.locations.map((loc) => (
            <div key={loc.id} className="flex items-center gap-3 px-3 py-2">
              <input
                type="radio"
                name="weather-location"
                checked={prefs.active_location_id === loc.id}
                onChange={() => update.mutate({ active_location_id: loc.id })}
                aria-label={`Show weather for ${loc.name}`}
              />
              <div className="flex min-w-0 flex-1 flex-col">
                <LocationName loc={loc} onRename={(name) => rename.mutate({ id: loc.id, name })} />
                <span className="truncate text-[0.75rem] text-[var(--text-faint)]">
                  {loc.region ?? `${loc.latitude.toFixed(3)}, ${loc.longitude.toFixed(3)}`}
                  {loc.fetched_at && `, updated ${format(new Date(loc.fetched_at), 'h:mm a').toLowerCase()}`}
                </span>
                {loc.fetch_error && (
                  <span className="text-[0.75rem] text-[var(--today-fg)]">{loc.fetch_error}</span>
                )}
              </div>
              <button
                onClick={() => setRemoving(loc)}
                className="rounded-md border border-[var(--border)] px-3 py-[4px] text-[0.78rem] text-[var(--text-muted)] hover:border-[var(--today-fg)] hover:text-[var(--today-fg)]"
              >
                Remove
              </button>
            </div>
          ))}
          {prefs.locations.length === 0 && (
            <div className="px-3 py-3 text-sm text-[var(--text-faint)]">No locations yet.</div>
          )}
        </div>
        <LocationSearch onAdded={invalidate} />
      </Section>

      <Section title="Forecast model">
        <p className="mb-3 text-[0.85rem] text-[var(--text-muted)]">
          Days past a model's range show no weather. Changing the model refetches every location.
        </p>
        <div className="flex flex-col divide-y divide-[var(--border)] overflow-hidden rounded-lg border border-[var(--border)]">
          {MODELS.map((m) => (
            <label
              key={m.id}
              className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-[var(--bg-hover)]"
            >
              <input
                type="radio"
                name="weather-model"
                className="mt-[3px]"
                checked={prefs.model === m.id}
                onChange={() => update.mutate({ model: m.id })}
              />
              <span className="flex flex-col">
                <span className="text-sm">{m.name}</span>
                <span className="text-[0.75rem] text-[var(--text-faint)]">{m.detail}</span>
              </span>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Units">
        <div className="flex flex-col gap-3">
          <Segmented
            label="Temperature"
            value={prefs.temperature_unit}
            options={[
              ['celsius', '°C'],
              ['fahrenheit', '°F']
            ]}
            onChange={(v) => update.mutate({ temperature_unit: v })}
          />
          <Segmented
            label="Wind"
            value={prefs.wind_speed_unit}
            options={[
              ['kmh', 'km/h'],
              ['ms', 'm/s'],
              ['mph', 'mph'],
              ['kn', 'knots']
            ]}
            onChange={(v) => update.mutate({ wind_speed_unit: v })}
          />
          <Segmented
            label="Precipitation"
            value={prefs.precipitation_unit}
            options={[
              ['mm', 'mm'],
              ['inch', 'inches']
            ]}
            onChange={(v) => update.mutate({ precipitation_unit: v })}
          />
          <Segmented
            label="Air quality index"
            value={prefs.aqi_scale}
            options={[
              ['us', 'US AQI'],
              ['european', 'European AQI']
            ]}
            onChange={(v) => update.mutate({ aqi_scale: v })}
          />
        </div>
      </Section>

      <Section title="Show weather in">
        <CheckList
          items={OVERLAYS}
          selected={prefs.overlays}
          onToggle={(id) => toggle('overlays', prefs.overlays, id)}
        />
      </Section>

      <Section title="Day cells">
        <p className="mb-3 text-[0.85rem] text-[var(--text-muted)]">
          Figures shown beside the condition icon in month cells and week headers. Zero chances and
          amounts are left out.
        </p>
        <CheckList
          items={CELL_STATS}
          selected={prefs.cell_stats}
          onToggle={(id) => toggle('cell_stats', prefs.cell_stats, id)}
        />
      </Section>

      <Section title="Weather details">
        <p className="mb-3 text-[0.85rem] text-[var(--text-muted)]">
          Rows in the popover that opens from the top bar or a day's weather.
        </p>
        <CheckList
          items={DETAIL_STATS}
          selected={prefs.detail_stats}
          onToggle={(id) => toggle('detail_stats', prefs.detail_stats, id)}
        />
      </Section>

      <p className="text-[0.75rem] text-[var(--text-faint)]">
        Forecasts, history and air quality from{' '}
        <a href="https://open-meteo.com/" target="_blank" rel="noreferrer" className="text-[var(--text-muted)]">
          Open-Meteo.com
        </a>
        , under CC BY 4.0. Air quality from CAMS.
      </p>

      <ConfirmDialog
        open={removing !== null}
        title={`Remove ${removing?.name ?? 'location'}?`}
        message="Its stored forecast and daily history are deleted too."
        confirmLabel="Remove"
        danger
        onConfirm={() => {
          if (removing) remove.mutate(removing.id);
          setRemoving(null);
        }}
        onClose={() => setRemoving(null)}
      />
    </>
  );
}

function LocationName({ loc, onRename }: { loc: WeatherLocation; onRename: (name: string) => void }) {
  const [draft, setDraft] = useState(loc.name);
  useEffect(() => setDraft(loc.name), [loc.name]);
  function commit() {
    const name = draft.trim();
    if (name && name !== loc.name) onRename(name);
    else setDraft(loc.name);
  }
  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft(loc.name);
          (e.target as HTMLInputElement).blur();
        }
      }}
      aria-label="Location name"
      className="-mx-1 min-w-0 rounded border border-transparent bg-transparent px-1 text-sm hover:border-[var(--border)] focus:border-[var(--border-strong)] focus:outline-none"
    />
  );
}

function LocationSearch({ onAdded }: { onAdded: () => void }) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const results = useQuery({
    queryKey: ['weather-geocode', debounced],
    queryFn: () => api.get<GeocodeResult[]>(`/api/weather/geocode?q=${encodeURIComponent(debounced)}`),
    enabled: debounced.length >= 2,
    staleTime: 5 * 60_000
  });
  const add = useMutation({
    mutationFn: (r: GeocodeResult) => api.post('/api/weather/locations', r),
    onSuccess: () => {
      setQuery('');
      onAdded();
    }
  });

  return (
    <div className="flex flex-col gap-2">
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Add a location: city, town, or postal code"
        className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-3 py-[8px] text-sm"
      />
      {debounced.length >= 2 && (
        <div className="flex flex-col overflow-hidden rounded-lg border border-[var(--border)]">
          {results.isLoading && <div className="px-3 py-2 text-sm text-[var(--text-faint)]">Searching…</div>}
          {results.isError && (
            <div className="px-3 py-2 text-sm text-[var(--today-fg)]">{(results.error as Error).message}</div>
          )}
          {results.data?.length === 0 && (
            <div className="px-3 py-2 text-sm text-[var(--text-faint)]">No places match.</div>
          )}
          {results.data?.map((r) => (
            <button
              key={`${r.latitude},${r.longitude}`}
              onClick={() => add.mutate(r)}
              disabled={add.isPending}
              className="flex flex-col items-start px-3 py-2 text-left hover:bg-[var(--bg-hover)] disabled:opacity-50"
            >
              <span className="text-sm">{r.name}</span>
              {r.region && <span className="text-[0.75rem] text-[var(--text-faint)]">{r.region}</span>}
            </button>
          ))}
        </div>
      )}
      {add.isError && <div className="text-[0.78rem] text-[var(--today-fg)]">{(add.error as Error).message}</div>}
    </div>
  );
}

function Segmented<T extends string>({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: T;
  options: [T, string][];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-[var(--text-muted)]">{label}</span>
      <div className="inline-flex rounded-md bg-[var(--bg-muted)] p-[2px]">
        {options.map(([v, text]) => (
          <button
            key={v}
            onClick={() => onChange(v)}
            className={clsx(
              'rounded px-3 py-[4px] text-[0.8rem]',
              value === v ? 'bg-[var(--bg)] text-[var(--text)] shadow-sm' : 'text-[var(--text-muted)] hover:text-[var(--text)]'
            )}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

function CheckList<T extends string>({
  items,
  selected,
  onToggle
}: {
  items: { id: T; label: string }[];
  selected: T[];
  onToggle: (id: T) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-[6px] sm:grid-cols-2">
      {items.map((it) => (
        <label key={it.id} className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={selected.includes(it.id)} onChange={() => onToggle(it.id)} />
          {it.label}
        </label>
      ))}
    </div>
  );
}
